import { fullNameOf } from './useOrgUsers'

const MAX_PICKER_RESULTS = 6

const matchesQuery = (user, lowerQuery) => {
  if (!lowerQuery) {
    return true
  }
  const name = fullNameOf(user).toLowerCase()
  return name.includes(lowerQuery) || (user.username || '').toLowerCase().includes(lowerQuery)
}

/**
 * Only users with Balto Cloud access can be tagged. `password` is the org
 * users endpoint's cloud-access boolean (same manager/agent split as
 * OrganizationPage's user counts) — agents come back false.
 *
 * Offering an agent is a dead end in both directions: notification-service
 * drops mention recipients without cloud access before it writes a bell row,
 * and no agent-facing surface renders a call comment. The tag posts, the
 * author sees a success toast, and the agent is never told.
 */
const canBeMentioned = (user) => Boolean(user.password)

/**
 * Filter org users for the `@`-mention picker: keep only users who can be
 * mentioned, drop the viewer themselves, narrow by the typed query (against
 * full name and username), cap at six.
 *
 * @param {Array<object>} users
 * @param {string} query
 * @param {number} currentUserId
 * @returns {Array<object>}
 */
export const filterUsersForPicker = (users, query, currentUserId) => {
  const lower = (query || '').toLowerCase()
  return users
    .filter(canBeMentioned)
    .filter((user) => user.id !== currentUserId)
    .filter((user) => matchesQuery(user, lower))
    .slice(0, MAX_PICKER_RESULTS)
}

/**
 * Locate the `@FullName` token in `text` whose range contains or ends at
 * `cursor`. Used so a single backspace/delete removes the entire mention
 * token (rather than peeling one character at a time).
 *
 * Takes the whole user list on purpose, unfiltered by `canBeMentioned` —
 * notes written before agents were excluded still carry agent tokens, and
 * editing one should delete the token whole like any other.
 *
 * @param {string} text
 * @param {number} cursor
 * @param {'backward'|'forward'} direction
 * @param {Array<object>} users
 * @returns {{start: number, end: number, userId: number} | null}
 */
export const findMentionAt = (text, cursor, direction, users) => {
  for (const user of users) {
    const name = fullNameOf(user)
    if (name) {
      const token = `@${name}`
      let idx = text.indexOf(token)
      while (idx !== -1) {
        const end = idx + token.length
        if (direction === 'backward' && cursor > idx && cursor <= end) {
          return { start: idx, end, userId: user.id }
        }
        if (direction === 'forward' && cursor >= idx && cursor < end) {
          return { start: idx, end, userId: user.id }
        }
        idx = text.indexOf(token, idx + 1)
      }
    }
  }
  return null
}

/**
 * True if at least one mentionable non-viewer org user matches `query` by
 * full name or username. Used to decide whether to keep the picker open as
 * the user types. Applies the same `canBeMentioned` gate as
 * `filterUsersForPicker` so a query that only matches agents closes the
 * picker instead of opening it on an empty list.
 *
 * @param {Array<object>} users
 * @param {string} query
 * @param {number} currentUserId
 * @returns {boolean}
 */
export const queryMatchesAnyone = (users, query, currentUserId) => {
  const lower = (query || '').toLowerCase()
  return users.some(
    (user) => canBeMentioned(user) && user.id !== currentUserId && matchesQuery(user, lower)
  )
}

/**
 * Inspect text around `cursor`: if there's an `@<query>` token (which may
 * include internal spaces, e.g. `@Tony G`) immediately before the caret AND
 * the typed query matches at least one non-viewer user, return the picker
 * state to open. Otherwise return null (the consumer should close the picker).
 *
 * @param {string} text
 * @param {number} cursor
 * @param {Array<object>} users
 * @param {number} currentUserId
 * @returns {{start: number, query: string} | null}
 */
export const computePickerState = (text, cursor, users, currentUserId) => {
  const slice = text.slice(0, cursor)
  const match = slice.match(/(?:^|\s)@([^\n@]*)$/)
  if (!match) {
    return null
  }
  const query = match[1]
  if (!queryMatchesAnyone(users, query, currentUserId)) {
    return null
  }
  return { start: cursor - query.length - 1, query }
}
